<svelte:options runes={false} />
<script>
  // Execution tape (TASK-20260823-twap-strategies-tca): the 1-minute low–high band with close as the
  // labelled mid proxy, the children's bid/ask at placement, one marker per filled child (hollow =
  // maker, solid = taker, grey = unknown — shape AND the readout name it, never color alone) and the
  // arrival / avg-fill / interval-VWAP / final reference lines. Inline SVG: every marker is a
  // focusable node with an accessible name, and the readout mirrors the focused/hovered fill.
  import { timeScale, linearScale, areaPath, linePath, extent, pad, niceDomain, formatTick, formatTimeTick } from '../charts/svgScales.js';
  import { formatQuoteAmount } from '../../../lib/quoteCurrency.js';
  import { fmtQty } from '../../../lib/twapReport.js';

  export let series = null;    // from tapeSeries(tape, children, parent, scorecard)
  export let currency = 'USD';

  const H = 240;
  const PAD = { top: 12, right: 64, bottom: 26, left: 8 };
  let width = 320;
  let active = null;
  // A marker is a real button: Enter/Space (or click) pins its readout so a keyboard user can read it
  // after moving on; the same key on the pinned marker releases it.
  let pinned = null;
  function togglePin(f) { pinned = pinned === f ? null : f; active = pinned ?? f; }
  function onMarkerKey(e, f) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); togglePin(f); }
  }

  $: hasData = (series?.band?.length ?? 0) > 0 || (series?.fills?.length ?? 0) > 0;
  $: times = [
    ...(series?.band ?? []).map((p) => p.t),
    ...(series?.fills ?? []).map((f) => f.t),
    ...(series?.quotes ?? []).map((q) => q.t),
    series?.window?.start, series?.window?.end,
  ].filter((t) => typeof t === 'number');
  $: prices = [
    ...(series?.band ?? []).flatMap((p) => [p.lo, p.hi, p.mid]),
    ...(series?.fills ?? []).map((f) => f.price),
    ...(series?.quotes ?? []).flatMap((q) => [q.bid, q.ask]),
    ...(series?.lines ?? []).map((l) => l.value),
  ];
  $: tDomain = extent(times);
  $: pDomain = niceDomain(...pad(extent(prices).map((v) => v ?? 0), 0.08), 5);
  $: x = timeScale([tDomain[0] ?? 0, tDomain[1] ?? 1], [PAD.left, Math.max(200, width) - PAD.right]);
  $: y = linearScale(pDomain, [H - PAD.bottom, PAD.top]);
  $: bandPath = areaPath(series?.band ?? [], (p) => x(p.t), (p) => y(p.hi), (p) => y(p.lo));
  $: midPath = linePath(series?.band ?? [], (p) => x(p.t), (p) => y(p.mid));
  $: xTicks = x.ticks(5);
  $: yTicks = y.ticks(5);
  $: plotRight = Math.max(200, width) - PAD.right;

  function markerLabel(f) {
    return `${f.style} fill · ${fmtQty(f.qty)} @ ${formatQuoteAmount(f.price, currency)} · ${new Date(f.t).toLocaleTimeString()}`;
  }
</script>

<div class="tape" bind:clientWidth={width}>
  {#if !hasData}
    <p class="tape-empty">No market tape or fills yet.</p>
  {:else}
    <svg class="tape-svg" viewBox={`0 0 ${Math.max(200, width)} ${H}`} width="100%" height={H} role="img"
      aria-label={`Execution tape: ${series.fills.length} fills over the window; ${series.midLabel}`}>
      {#each yTicks as t}
        <line x1={PAD.left} x2={plotRight} y1={y(t)} y2={y(t)} class="tape-grid" />
        <text x={plotRight + 4} y={y(t) + 3} class="tape-tick">{formatTick(t)}</text>
      {/each}
      {#each xTicks as t}
        <text x={x(t)} y={H - 8} class="tape-tick" text-anchor="middle">{formatTimeTick(t)}</text>
      {/each}
      {#if series.window}
        <rect x={x(series.window.start)} y={PAD.top} width={Math.max(0, x(series.window.end) - x(series.window.start))} height={H - PAD.top - PAD.bottom} class="tape-window" />
      {/if}
      <path d={bandPath} class="tape-band" />
      <path d={midPath} class="tape-mid" />
      {#each series.quotes as q}
        <line x1={x(q.t)} x2={x(q.t)} y1={y(q.ask)} y2={y(q.bid)} class="tape-quote" />
      {/each}
      {#each series.lines as l}
        <line x1={PAD.left} x2={plotRight} y1={y(l.value)} y2={y(l.value)} class={`tape-line tape-line-${l.key}`} />
        <text x={PAD.left + 4} y={y(l.value) - 3} class={`tape-line-label tape-line-${l.key}`}>{l.label} {formatTick(l.value)}</text>
      {/each}
      {#each series.fills as f (f.childOrderId)}
        <circle cx={x(f.t)} cy={y(f.price)} r={active?.childOrderId === f.childOrderId ? 6 : 4.5}
          class={`tape-fill tape-fill-${f.style}`} tabindex="0" role="button" aria-label={markerLabel(f)}
          aria-pressed={pinned === f}
          on:mouseenter={() => (active = f)} on:mouseleave={() => (active = pinned)}
          on:focus={() => (active = f)} on:blur={() => (active = pinned)}
          on:click={() => togglePin(f)} on:keydown={(e) => onMarkerKey(e, f)}>
          <title>{markerLabel(f)}</title>
        </circle>
      {/each}
    </svg>
    <div class="tape-foot">
      <span class="tape-legend"><span class="sw sw-maker"></span> maker (hollow)</span>
      <span class="tape-legend"><span class="sw sw-taker"></span> taker (solid)</span>
      <span class="tape-legend"><span class="sw sw-unknown"></span> unknown (grey)</span>
      <span class="tape-legend tape-note">{series.midLabel}</span>
    </div>
    <div class="tape-readout" aria-live="polite">{active ? markerLabel(active) : ' '}</div>
  {/if}
</div>

<style>
  .tape { width: 100%; min-width: 0; }
  .tape-svg { display: block; border: 1px solid var(--border-primary, #1e1f22); border-radius: var(--radius-base, 0.5rem); background: var(--bg-secondary, #111214); }
  .tape-grid { stroke: var(--chart-grid, #1e1f22); }
  .tape-tick { fill: var(--text-secondary, #8a8b8d); font-size: 10px; font-variant-numeric: tabular-nums; }
  .tape-window { fill: var(--color-primary-alpha-10, rgba(0, 212, 255, 0.1)); }
  .tape-band { fill: var(--text-muted, #6a6b6d); opacity: 0.18; }
  .tape-mid { fill: none; stroke: var(--text-secondary, #8a8b8d); stroke-width: 1.2; }
  .tape-quote { stroke: var(--color-primary, #00d4ff); stroke-width: 1.5; opacity: 0.7; }
  .tape-line { stroke-width: 1; stroke-dasharray: 4 3; }
  .tape-line-label { font-size: 9px; }
  .tape-line-arrival { stroke: var(--color-warning, #ffaa00); fill: var(--color-warning, #ffaa00); }
  .tape-line-avgFill { stroke: var(--text-primary, #fff); fill: var(--text-primary, #fff); }
  .tape-line-vwap { stroke: var(--color-primary, #00d4ff); fill: var(--color-primary, #00d4ff); }
  .tape-line-final { stroke: var(--text-secondary, #8a8b8d); fill: var(--text-secondary, #8a8b8d); }
  .tape-fill { stroke-width: 1.8; cursor: pointer; }
  .tape-fill:focus-visible { outline: none; stroke-width: 3; }
  .tape-fill-maker { fill: var(--bg-secondary, #111214); stroke: var(--color-success, #00ff88); }
  .tape-fill-taker { fill: var(--color-danger, #ff3b3b); stroke: var(--color-danger, #ff3b3b); }
  .tape-fill-unknown { fill: var(--text-muted, #6a6b6d); stroke: var(--text-muted, #6a6b6d); }
  .tape-foot { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 0.3rem; font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); }
  .tape-legend { display: inline-flex; align-items: center; gap: 0.3rem; }
  .tape-note { color: var(--text-muted, #6a6b6d); }
  .sw { width: 10px; height: 10px; border-radius: 50%; display: inline-block; box-sizing: border-box; }
  .sw-maker { border: 2px solid var(--color-success, #00ff88); }
  .sw-taker { background: var(--color-danger, #ff3b3b); }
  .sw-unknown { background: var(--text-muted, #6a6b6d); }
  .tape-readout { min-height: 1.1rem; font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); font-variant-numeric: tabular-nums; padding-top: 0.2rem; }
  .tape-empty { color: var(--text-muted, #6a6b6d); font-size: var(--font-size-sm, 0.85rem); }
</style>
